import { RefObject, useEffect, useMemo, useRef, useState } from 'react'

import { useDocumentState } from '@/state'
import { TemplateAttribute } from '@/services/hooks/template_attributes'
import { getDocumentAnnotationBounds } from './annotationGeometry'
import { toRenderableKonvaAnnotation } from './legacyAnnotationMappers'

type VirtualizedKonvaAnnotation = {
  bottom: number
  templateId: string
  top: number
}

type UseVirtualizedKonvaAnnotationsProps = {
  annotationsContainerRef: RefObject<HTMLDivElement | null>
  forceMountedIds?: Set<string>
  subPageIds: string[]
  templateAttributes: TemplateAttribute<any>[] | undefined
  printSubPageId?: string
  printFirstSubPageId?: string
  documentPageId?: string
}

const OVERSCAN_PIXELS = 1000

const areSetsEqual = (a: Set<string>, b: Set<string>) => {
  if (a.size !== b.size) return false

  for (const value of a) {
    if (!b.has(value)) return false
  }

  return true
}

export const useVirtualizedKonvaAnnotations = ({
  annotationsContainerRef,
  forceMountedIds = new Set(),
  subPageIds,
  templateAttributes,
  printSubPageId,
  printFirstSubPageId,
  documentPageId,
}: UseVirtualizedKonvaAnnotationsProps) => {
  const [viewportInfo, setViewportInfo] = useState({
    pageOffsetY: 0,
    scrollTop: 0,
    viewportHeight: 0,
  })

  const virtualizer = useDocumentState((state) => state.virtualizer)
  const zoom = useDocumentState((state) => state.zoom)
  const zoomFactor = zoom / 100

  // Set for O(1) membership checks — only visible subPageIds are included
  const subPageIdSet = useMemo(() => new Set(subPageIds), [subPageIds])

  const annotationsWithBounds = useMemo<VirtualizedKonvaAnnotation[]>(() => {
    if (!templateAttributes || !subPageIds.length) return []

    return templateAttributes
      .map((templateAttribute) => {
        if (templateAttribute.data_type !== 'annotation') {
          return null
        }

        // Filter out annotations that belong to a hidden subpage
        const annotationSubPageId = templateAttribute.template_values?.subPageId
        if (printSubPageId) {
          // When subPageId is not set, annotation defaults to the document page (default subpage)
          const resolvedSubPageId = annotationSubPageId ?? documentPageId
          if (resolvedSubPageId !== printSubPageId) {
            return null
          }
        }
        if (annotationSubPageId && !subPageIdSet.has(annotationSubPageId)) {
          return null
        }

        const annotation = toRenderableKonvaAnnotation(
          templateAttribute.template_values,
        )
        const bounds = getDocumentAnnotationBounds({ annotation, subPageIds })
        const top = bounds.top * zoomFactor + viewportInfo.pageOffsetY
        const bottom =
          (bounds.top + bounds.height) * zoomFactor + viewportInfo.pageOffsetY

        return {
          bottom,
          templateId: templateAttribute.id as string,
          top,
        }
      })
      .filter((item): item is VirtualizedKonvaAnnotation => Boolean(item))
  }, [
    viewportInfo.pageOffsetY,
    subPageIds,
    subPageIdSet,
    templateAttributes,
    zoomFactor,
    printSubPageId,
    printFirstSubPageId,
    documentPageId,
  ])

  useEffect(() => {
    if (!virtualizer || !annotationsContainerRef.current) return

    const scrollElement = virtualizer.scrollElement
    if (!scrollElement || !(scrollElement instanceof HTMLElement)) return

    let frameId: number | null = null

    const updateViewportInfo = () => {
      const containerRect =
        annotationsContainerRef.current?.getBoundingClientRect()
      const scrollRect = scrollElement.getBoundingClientRect()

      if (!containerRect || !scrollRect) return

      const nextViewportInfo = {
        pageOffsetY:
          containerRect.top - scrollRect.top + scrollElement.scrollTop,
        scrollTop: scrollElement.scrollTop,
        viewportHeight: scrollElement.clientHeight,
      }

      setViewportInfo((currentViewportInfo) =>
        currentViewportInfo.pageOffsetY === nextViewportInfo.pageOffsetY &&
        currentViewportInfo.scrollTop === nextViewportInfo.scrollTop &&
        currentViewportInfo.viewportHeight === nextViewportInfo.viewportHeight
          ? currentViewportInfo
          : nextViewportInfo,
      )
    }

    const scheduleUpdateViewportInfo = () => {
      if (frameId !== null) return

      frameId = requestAnimationFrame(() => {
        frameId = null
        updateViewportInfo()
      })
    }

    updateViewportInfo()
    scrollElement.addEventListener('scroll', scheduleUpdateViewportInfo, {
      passive: true,
    })
    window.addEventListener('resize', scheduleUpdateViewportInfo, {
      passive: true,
    })

    return () => {
      if (frameId !== null) {
        cancelAnimationFrame(frameId)
      }

      scrollElement.removeEventListener('scroll', scheduleUpdateViewportInfo)
      window.removeEventListener('resize', scheduleUpdateViewportInfo)
    }
  }, [annotationsContainerRef, virtualizer])

  const visibleAnnotationIdsRef = useRef<Set<string>>(new Set())
  const visibleAnnotationIds = useMemo(() => {
    const visibleTop = viewportInfo.scrollTop - OVERSCAN_PIXELS
    const visibleBottom =
      viewportInfo.scrollTop + viewportInfo.viewportHeight + OVERSCAN_PIXELS
    const nextVisibleAnnotationIds =
      viewportInfo.viewportHeight === 0
        ? new Set(annotationsWithBounds.map((item) => item.templateId))
        : new Set(
            annotationsWithBounds
              .filter((item) => {
                if (forceMountedIds.has(item.templateId)) return true

                return item.bottom >= visibleTop && item.top <= visibleBottom
              })
              .map((item) => item.templateId),
          )

    if (
      areSetsEqual(visibleAnnotationIdsRef.current, nextVisibleAnnotationIds)
    ) {
      return visibleAnnotationIdsRef.current
    }

    visibleAnnotationIdsRef.current = nextVisibleAnnotationIds
    return nextVisibleAnnotationIds
  }, [annotationsWithBounds, forceMountedIds, viewportInfo])

  return { visibleAnnotationIds }
}
